import React from "react";
import { ElegantTooltip } from "../../../../shared/formComponents/ElegantTooltip/ElegantTooltip";
import {
  REVIEW_STATUS,
  WORKFLOW_STATUS,
} from "../../_config/quarterlyOkrConfig";
import "./_qokr-avatar.scss";
import "./_qokr-pill.scss";
import "./_qokr-link-chip.scss";

// ═══════════════════════════════════════════════════════════════════════════
// Small pieces every Quarterly OKR screen uses. People are always the BE's
// `{ user_id, name, is_deactivated }`; objectives are the board / card shape.
// ═══════════════════════════════════════════════════════════════════════════

// A disabled control says why. Wraps only when there is a reason.
export const Explained = ({ reason, children, placement = "top" }) =>
  reason ? (
    <ElegantTooltip content={reason} placement={placement}>
      <span className="d-inline-flex">{children}</span>
    </ElegantTooltip>
  ) : (
    children
  );

// Focus a field when it first appears (`autoFocus` is barred by jsx-a11y).
export const focusOnMount = (node) => {
  if (!node || node.dataset.focused) return;
  node.dataset.focused = "1";
  node.focus();
  if (typeof node.setSelectionRange === "function" && node.value != null) {
    node.setSelectionRange(node.value.length, node.value.length);
  }
};

export const initialsOf = (name = "") =>
  name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join("");

export const personLabel = (person) =>
  person?.is_deactivated ? `${person.name} (deactivated)` : person?.name || "";

// ── Avatar ──────────────────────────────────────────────────────────────────
export const QokrAvatar = ({ person, size = "md" }) => (
  <span
    className={`qokr-avatar qokr-avatar--${size}${
      person?.is_deactivated ? " qokr-avatar--deactivated" : ""
    }`}
    title={personLabel(person) || undefined}
  >
    {person?.name ? (
      initialsOf(person.name)
    ) : (
      <i className="fa-solid fa-user" aria-hidden="true" />
    )}
  </span>
);

// Owner in a meta line, as on the Annual OKR board row.
export const PersonInline = ({ person }) =>
  person ? (
    <span className="cob__person">
      <span className="cob__person-avatar" aria-hidden="true">
        {initialsOf(person.name)}
      </span>
      {personLabel(person)}
    </span>
  ) : null;

// ── State pill ──────────────────────────────────────────────────────────────
// One look per objective state everywhere it appears (chat card, board).
const PILL = {
  editing: { label: "Editing", tone: "teal", dot: true },
  in_interview: { label: "In interview", tone: "teal", dot: true },
  [WORKFLOW_STATUS.AI_DRAFTED]: {
    label: "AI draft",
    tone: "grey",
    icon: "fa-wand-magic-sparkles",
  },
  [WORKFLOW_STATUS.SUBMITTED]: {
    label: "Accepted",
    tone: "green",
    icon: "fa-check",
  },
  [WORKFLOW_STATUS.REVIEWED]: {
    label: "Reviewed",
    tone: "blue",
    icon: "fa-user-check",
  },
  [WORKFLOW_STATUS.FINALIZED]: {
    label: "Locked",
    tone: "green",
    icon: "fa-lock",
  },
  // Coach marks (§9).
  [REVIEW_STATUS.ACCEPTED]: {
    label: "Accepted",
    tone: "green",
    icon: "fa-check",
  },
  [REVIEW_STATUS.TO_DISCUSS]: {
    label: "To discuss",
    tone: "amber",
    icon: "fa-comment-dots",
  },
  discussed: { label: "Discussed", tone: "green", icon: "fa-check-double" },
};

export const StatePill = ({ state }) => {
  const config = PILL[state];
  if (!config) return null;
  return (
    <span className={`qokr-pill qokr-pill--${config.tone}`}>
      {config.dot ? <span className="qokr-pill__dot" /> : null}
      {config.icon ? (
        <i className={`fa-solid ${config.icon}`} aria-hidden="true" />
      ) : null}
      {config.label}
    </span>
  );
};

// ── Link chip ───────────────────────────────────────────────────────────────
// What an objective serves: an Annual OKR (company step) or a company
// objective (individual step) — or nothing, which is fine for an extra one.
export const LinkChip = ({
  label,
  kind = "company",
  relationship = null,
  focusable = true,
}) => {
  const linked = Boolean(label);
  const eyebrow = !linked
    ? "Not linked"
    : kind === "company"
      ? "Linked Annual OKR"
      : `Linked company objective${relationship ? ` · you're the ${relationship}` : ""}`;
  const title = linked
    ? label
    : kind === "company"
      ? "Not linked to an Annual OKR — fine for an extra objective this quarter."
      : "Not linked to a company objective.";

  return (
    <ElegantTooltip
      maxWidth={300}
      content={
        <span className="qokr-link-tip">
          <span className="qokr-link-tip__eyebrow">{eyebrow}</span>
          <span className="qokr-link-tip__title">{title}</span>
        </span>
      }
    >
      <span
        className={`qokr-link-chip${linked ? "" : " qokr-link-chip--none"}`}
        // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
        tabIndex={focusable ? 0 : undefined}
        aria-label={focusable ? `${eyebrow}: ${title}` : undefined}
      >
        <i
          className={`fa-solid ${linked ? "fa-link" : "fa-link-slash"}`}
          aria-hidden="true"
        />
      </span>
    </ElegantTooltip>
  );
};

// ── Contributors, compact ───────────────────────────────────────────────────
// Up to `max` faces, "+N" and the count; full names in the tooltip.
export const ContributorStack = ({ people = [], max = 4 }) => {
  if (!people.length) return null;
  const shown = people.slice(0, max);
  const rest = people.length - shown.length;
  return (
    <ElegantTooltip
      maxWidth={260}
      trigger={["hover"]}
      content={
        <span className="qokr-link-tip">
          <span className="qokr-link-tip__eyebrow">
            Contributors · {people.length}
          </span>
          {people.map((person) => (
            <span key={person.user_id} className="qokr-link-tip__title">
              {personLabel(person)}
            </span>
          ))}
        </span>
      }
    >
      <span
        className="qokr-cstack"
        aria-label={`Contributors: ${people.map(personLabel).join(", ")}`}
      >
        <span className="qokr-cstack__faces" aria-hidden="true">
          {shown.map((person) => (
            <QokrAvatar key={person.user_id} person={person} size="xs" />
          ))}
          {rest > 0 ? <span className="qokr-cstack__more">+{rest}</span> : null}
        </span>
        {people.length} contributor{people.length === 1 ? "" : "s"}
      </span>
    </ElegantTooltip>
  );
};
